import React, { useState, useMemo, useRef, useEffect, useId } from 'react'
import {
  Box,
  OutlinedInput,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  TextField,
  Collapse,
  Button,
  FormControlLabel,
  Checkbox,
  Switch,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
  ListItemText,
  Popover,
  useMediaQuery,
} from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import StraightenIcon from '@mui/icons-material/Straighten'
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined'
import GridOnIcon from '@mui/icons-material/GridOn'
import LegendToggleIcon from '@mui/icons-material/LegendToggle'
import NumbersIcon from '@mui/icons-material/Numbers'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'
import TuneIcon from '@mui/icons-material/Tune'
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'

import Tree from '../../tree/Tree'
import { GenNode } from '../../../types/carbon/models/GenNode'
import {
  BrandTheme,
  TableGraphWidgetData,
  TableGraphSelection,
  TableViewConfig,
  TableViewSettingsData,
  ChartDesign,
  TableDesign,
  TableConditionalFormatRule,
  ChartSortOrder,
  ChartCategoryLabelMode,
} from '../../../types/gotypes'
import { getAugementedTOC } from '../../../utils/multitables'
import { getInitialExpandedIds } from '../../../utils/tree'
import { DashboardJobSelect, useDashboardJobs } from '../DashboardJobs'
import {
  effectiveSelection,
  resolveSelection,
  removeSeriesFromSelection,
  defaultDataOnlySelection,
  chartableAxis,
  effectiveSelectionPositions,
  buildPositionalSelection,
  isSingleColumnChartType,
} from '../../../utils/tableSelection'
import {
  extractColumnLabels,
  extractColumnTypes,
} from '../../platinum-graphs/utils/convertPlatinumData'
import { blankZeroBasePoints } from '../../../utils/skipZeroBase'
import { lowBaseReport, LOW_BASE_THRESHOLD, LOW_CELL_THRESHOLD } from '../../../utils/lowBase'
import {
  applySelectionToTableData,
  applyTableDataType,
  applyViewConfigToPlatinumData,
  cleanOrphanedReferences,
  isFrozen,
  resolveColumnWidths,
  resolveGroupLabelVisibility,
  resolveTableDataTypes,
  stripColumnGroupLabels,
  stripRowGroupLabels,
  transposePlatinumData,
  withDefaultGroupLabelVisibility,
} from '../../../utils/tableViewConfig'
import {
  resolveChartDesign,
  setChartDesignProperty,
  revertChartDesignProperty,
  revertAllChartDesign,
  revertChartDesignTitleGroup,
  isChartDesignPropertyOverridden,
  ChartDesignPropertyPath,
  addBarColorRule,
  updateBarColorRule,
  removeBarColorRule,
  setBarColorDefault,
} from '../../../utils/chartDesign'
import { isBarFamilyChartType, legacySortOrder } from '../../platinum-graphs/utils/chartSort'
import { chartDataWithCategoryLabels } from '../../../utils/chartCategoryLabels'
import { ConditionalFormatRuleList } from './ConditionalFormatRuleList'
import {
  resolveTableDesign,
  setTableDesignProperty,
  revertTableDesignProperty,
  revertAllTableDesign,
  isTableDesignPropertyOverridden,
  addConditionalFormatRule,
  updateConditionalFormatRule,
  removeConditionalFormatRule,
  TableDesignPropertyPath,
} from '../../../utils/tableDesign'
import { suggestChartType } from '../../../utils/chartTypeSuggestion'
import { isTableNotFoundError } from '../../../utils/tableErrors'
import {
  captureChartTemplate,
  chartTemplateHasContent,
  chartTemplateApplyWouldOverwrite,
  applyChartTemplateToWidgetData,
} from '../../../utils/chartTemplate'
import useTableData from '../../../hooks/useTableData'
import useAccount from '../../../hooks/useAccount'
import useSnackbar from '../../../hooks/useSnackbar'
import useChartTemplates from '../../../hooks/useChartTemplates'
import { PlatinumTableWrapper } from '../../platinum-table-new/PlatinumTableWrapper'
import { TableCitationModal } from './TableCitationModal'
import { FilterSignpostLine, useFilterSignpost } from '../FilterSignpost'
import { TableSelectionEditor } from './TableSelectionEditor'
import { TableSelectDataPanel } from './TableSelectDataPanel'
import { EditDataOverlay } from './EditDataOverlay'
import { API_BASE_URL } from '../../../constants/system'
import { ChartType, DataType, CHART_TYPE_LABELS, DATA_TYPE_OPTIONS } from '../../platinum-graphs/types'
import { buildColorMap } from '../../platinum-graphs/generators/helpers'

/**
 * One design-control row: a label, a control (passed as children), and a
 * "revert to theme" affordance that only appears once the property carries
 * an explicit override (#968 Part C — per-property revert).
 */
const DesignControlRow: React.FC<{
  label: string
  overridden: boolean
  onRevert: () => void
  children: React.ReactNode
}> = ({ label, overridden, onRevert, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    {children}
    <IconButton
      size="small"
      onClick={onRevert}
      disabled={!overridden}
      title="Revert to theme"
      sx={{ visibility: overridden ? 'visible' : 'hidden' }}
    >
      <RestartAltIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
)

/**
 * One Chart Design group in the #1174 title-row treatment (Matt's 1 Sep
 * request on #1174/#1172): an icon + name header carrying ONE revert for the
 * whole group, and the controls in a compact wrapping row beneath — replacing
 * the stacked label/control/revert rows that made the section a long scroll.
 * The stored per-property model in chartDesign.ts is unchanged; only the
 * editor's grouping is.
 */
const DesignGroup: React.FC<{
  icon: React.ReactNode
  title: string
  overridden: boolean
  onRevert: () => void
  /** Replaces the standard revert icon — the value-axis group keeps its
   *  Matt-approved "Auto" button (#1178). */
  revertControl?: React.ReactNode
  testId?: string
  children: React.ReactNode
}> = ({ icon, title, overridden, onRevert, revertControl, testId, children }) => (
  <Box data-testid={testId}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Box sx={{ display: 'flex', color: 'text.secondary', '& svg': { fontSize: 16 } }}>{icon}</Box>
      <Typography variant="caption" fontWeight={600}>{title}</Typography>
      <Box sx={{ flex: 1 }} />
      {revertControl ?? (
        <Tooltip title={`Revert ${title} to theme`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Revert ${title}`}
              onClick={onRevert}
              disabled={!overridden}
            >
              <RestartAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      {children}
    </Box>
  </Box>
)

/**
 * The right-hand configuration pane (#1218): every control that is not the
 * preview lives here, in collapsible sections with their own scroll bar, so
 * the chart never scrolls out of view while it is being configured. The pane
 * starts collapsed to an icon rail ("defer expansion until requested" — 2 Sep
 * call) and remembers its state per user.
 */
type ConfigSectionId =
  | 'data' | 'theme' | 'axis' | 'labels' | 'legend' | 'gridlines' // graph mode
  | 'styling' | 'design'                                          // table mode

interface ConfigSectionDef {
  id: ConfigSectionId
  title: string
  icon: React.ReactNode
}

const CONFIG_PANE_STORE_KEY = 'dataviz-chart-config-pane'

function loadPaneState(): { open: boolean; sections: ConfigSectionId[] } {
  try {
    const raw = localStorage.getItem(CONFIG_PANE_STORE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (typeof parsed?.open === 'boolean' && Array.isArray(parsed?.sections)) return parsed
    }
  } catch { /* fall through to the default */ }
  return { open: false, sections: ['data'] }
}

function storePaneState(open: boolean, sections: ConfigSectionId[]) {
  try {
    localStorage.setItem(CONFIG_PANE_STORE_KEY, JSON.stringify({ open, sections }))
  } catch { /* localStorage unavailable — state is per-render only */ }
}

/**
 * One collapsible section of the configuration pane. The header button
 * carries the SAME accessible name as the section's rail icon ("<Title>
 * settings"), so "open the <x> settings" is one gesture whether the pane is
 * collapsed or not.
 */
const ConfigSection: React.FC<{
  def: ConfigSectionDef
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ def, open, onToggle, children }) => (
  <Box data-testid={`config-section-${def.id}`} sx={{ borderBottom: 1, borderColor: 'divider' }}>
    <Box
      component="button"
      type="button"
      aria-label={`${def.title} settings`}
      aria-expanded={open}
      onClick={onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, width: '100%',
        border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
        px: 1.5, py: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ display: 'flex', color: 'text.secondary', '& svg': { fontSize: 18 } }}>{def.icon}</Box>
      <Typography variant="subtitle2" sx={{ flex: 1 }}>{def.title}</Typography>
      {open ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
    </Box>
    <Collapse in={open}>
      <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </Box>
    </Collapse>
  </Box>
)

/** Section catalogue (2 Sep call): Data, Theme, Axis, Labels, Legend,
 *  Gridlines for charts; Data, Styling, Design for tables. */
const CONFIG_SECTION_DEFS: Record<ConfigSectionId, ConfigSectionDef> = {
  data: { id: 'data', title: 'Data', icon: <TuneIcon /> },
  theme: { id: 'theme', title: 'Theme', icon: <PaletteOutlinedIcon /> },
  axis: { id: 'axis', title: 'Axis', icon: <StraightenIcon /> },
  labels: { id: 'labels', title: 'Labels', icon: <LabelOutlinedIcon /> },
  legend: { id: 'legend', title: 'Legend', icon: <LegendToggleIcon /> },
  gridlines: { id: 'gridlines', title: 'Gridlines', icon: <GridOnIcon /> },
  styling: { id: 'styling', title: 'Styling', icon: <TableChartIcon /> },
  design: { id: 'design', title: 'Design', icon: <PaletteOutlinedIcon /> },
}
const GRAPH_PANE_SECTIONS: ConfigSectionId[] = ['data', 'theme', 'axis', 'labels', 'legend', 'gridlines']
const TABLE_PANE_SECTIONS: ConfigSectionId[] = ['data', 'styling', 'design']

/**
 * Numeric override field for the value-axis scale controls (#1178).
 *
 * Holds its own text state so partially-typed numbers ("0.") survive the
 * keystroke, commits only values that pass `validate`, and treats an empty
 * field as "revert to Auto" (commit undefined) — Auto is the absence of an
 * override, per the existing revert model. An EXTERNAL change to the
 * override (the group Auto button, a per-row revert) resyncs the text via a
 * render-phase guard, never an effect.
 */
const NumericDesignField: React.FC<{
  value: number | undefined
  onCommit: (value: number | undefined) => void
  /** Returns an error message for a refused value, or null to accept. */
  validate?: (value: number) => string | null
  ariaLabel: string
  width?: number
  /** Outlined field label — identifies the field in the compact group row
   *  (#1174) where there is no side caption. */
  label?: string
  disabled?: boolean
}> = ({ value, onCommit, validate, ariaLabel, width = 110, label, disabled }) => {
  const [text, setText] = useState(value != null ? String(value) : '')
  const [error, setError] = useState<string | null>(null)
  const lastValueRef = React.useRef(value)
  if (lastValueRef.current !== value) {
    lastValueRef.current = value
    const parsed = text.trim() === '' ? undefined : Number(text)
    // Resync only when the override moved away from what this field says —
    // our own commits round-trip and must not stomp in-progress typing.
    if (parsed !== value && !(Number.isNaN(parsed as number) && value === undefined)) {
      setText(value != null ? String(value) : '')
      setError(null)
    }
  }
  return (
    <TextField
      size="small"
      value={text}
      placeholder="Auto"
      label={label}
      disabled={disabled}
      InputLabelProps={label ? { shrink: true } : undefined}
      error={Boolean(error)}
      helperText={error || undefined}
      onChange={(e) => {
        const raw = e.target.value
        setText(raw)
        if (raw.trim() === '') {
          setError(null)
          onCommit(undefined)
          return
        }
        const parsed = Number(raw)
        if (Number.isNaN(parsed)) {
          setError('Enter a number')
          return
        }
        const message = validate ? validate(parsed) : null
        if (message) {
          // Refused, not saved (#1178 validation) — the chart keeps its
          // last good value until the field parses clean again.
          setError(message)
          return
        }
        setError(null)
        onCommit(parsed)
      }}
      sx={{ width }}
      inputProps={{ 'aria-label': ariaLabel, inputMode: 'decimal' }}
    />
  )
}

interface TableGraphWidgetEditorProps {
  customer: string
  job: string
  dashboardId: string
  /** Dashboard's display name — names the Edit Data save folder (#968 Part B). */
  dashboardName: string
  data: TableGraphWidgetData | undefined
  onDataChange: (data: TableGraphWidgetData) => void
  /** The customer's saved brand themes (#1193) — options for the chart theme selector. */
  themes?: BrandTheme[]
  /** Name of the page's applied theme, for the Chart Styling display line (#1175). */
  pageThemeName?: string
  /**
   * The PAGE-level colour map (job palette + page theme), so the Preview
   * shows the same colours as the dashboard (#1171/#1193).
   */
  colorMap?: Record<string, string>
  /** Theme id → flattened colour map, for previewing this chart's own theme. */
  themeColorMaps?: Record<string, Record<string, string>>
}

/**
 * Editor component for configuring a table/graph widget.
 * Multi-step: table selection → view mode & chart config → row/column view filter.
 */
export const TableGraphWidgetEditor: React.FC<TableGraphWidgetEditorProps> = ({
  customer,
  job,
  dashboardId,
  dashboardName,
  data,
  onDataChange,
  themes = [],
  pageThemeName,
  colorMap,
  themeColorMaps,
}) => {
  const [expanded, setExpanded] = useState<string[]>([])
  const [selected, setSelected] = useState<string>('')
  const [colorOverridesOpen, setColorOverridesOpen] = useState(false)
  // #1175: clicking a theme name in Chart Styling opens a swatch peek.
  const [themePeek, setThemePeek] = useState<{ anchor: HTMLElement; theme: BrandTheme } | null>(null)
  const [newOverrideName, setNewOverrideName] = useState('')
  const [newOverrideColor, setNewOverrideColor] = useState('#4E79A7')
  const [editDataOpen, setEditDataOpen] = useState(false)
  const chartTypeLabelId = useId()
  const dataTypeLabelId = useId()
  const chartThemeLabelId = useId()
  const labelIntervalLabelId = useId()
  const dashStyleLabelId = useId()
  const legendPositionLabelId = useId()
  const numberFormatLabelId = useId()
  const sortOrderLabelId = useId()
  const categoryLabelLabelId = useId()
  // #1218: configuration pane state, persisted per user. Collapsed by
  // default — the pane expands when asked, and the preview keeps the space.
  const [paneState, setPaneState] = useState(loadPaneState)
  const setPane = (open: boolean, sections: ConfigSectionId[]) => {
    setPaneState({ open, sections })
    storePaneState(open, sections)
  }
  const togglePaneSection = (id: ConfigSectionId) => {
    const sections = paneState.sections.includes(id)
      ? paneState.sections.filter((s) => s !== id)
      : [...paneState.sections, id]
    setPane(paneState.open, sections)
  }
  const openPaneAt = (id: ConfigSectionId) => {
    const sections = paneState.sections.includes(id)
      ? paneState.sections
      : [...paneState.sections, id]
    setPane(true, sections)
  }
  // Below ~1100px the OPEN pane overlays the preview instead of squeezing it
  // (Matt's answer to #1218 Q3).
  const paneOverlays = useMediaQuery('(max-width: 1099.95px)')
  // Chart templates (#1050) — "Save as chart template" / "Apply template".
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false)
  const [templateNameInput, setTemplateNameInput] = useState('')
  const [templateDescriptionInput, setTemplateDescriptionInput] = useState('')
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false)
  const [pendingApplyTemplateId, setPendingApplyTemplateId] = useState('')
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false)
  // #968 Part D: after the user picks a table, suggest a chart type from its
  // shape — once, and only until they touch the Chart Type dropdown. A ref,
  // not state: it must never re-render or re-fire on its own.
  const suggestPendingRef = useRef(false)
  const dataRef = useRef(data)
  dataRef.current = data
  const onDataChangeRef = useRef(onDataChange)
  onDataChangeRef.current = onDataChange

  // Table selection: a single-job dashboard is locked to its own job (#961);
  // a multi-job dashboard (#1162) offers a job selector above the tree
  // (#1229 C4) — pick the job, then the table. `treeJob` is the job the tree
  // is browsing; a picked table is stamped with it. Always fetch the TOC
  // directly rather than trusting account.toc: that's only ever populated by
  // visiting the Tables page for a job (see contexts/account.tsx's reloadTOC
  // — nothing else calls it), so relying on it here left this tree empty
  // with zero network requests whenever a dashboard was edited without a
  // prior Tables visit for the same job in this session.
  const dashboardJobs = useDashboardJobs()
  const [pickJob, setPickJob] = useState(data?.job || job)
  const treeJob = (dashboardJobs?.jobs.some((j) => j.name === pickJob) ? pickJob : '') || data?.job || job
  const tocQuery = useQuery({
    queryKey: ['toc', customer, treeJob],
    enabled: Boolean(customer && treeJob),
    queryFn: async () => {
      const res = await axios.get<{ toc: GenNode[] }>(
        `${API_BASE_URL}/job/${customer}/${treeJob}/toc`
      )
      return res.data.toc || []
    },
  })

  const rawToc = tocQuery.data || []
  const tocLoading = tocQuery.isLoading

  // #968 comment 1: name the job the way the top-banner picker does — the
  // session job list's displayName (pages/Layout.tsx reads the same field),
  // falling back to the slug when a job has no display name.
  const account = useAccount()
  const jobLabel = (name: string | undefined): string => {
    if (!name) return ''
    return account.customer?.sessionJobs?.find(j => j.name === name)?.displayName
      || (account.job?.name === name ? account.job?.displayName : undefined)
      || name
  }
  const augmentedToc = useMemo(() => {
    return getAugementedTOC({
      toc: rawToc,
      multiTables: [],
    })
  }, [rawToc])

  const toc = augmentedToc.nodes

  // #1229 B: open the folders the TOC flags expanded, once per loaded TOC
  // (render-phase ref guard — no effect needed, nothing to clean up).
  const expandedInitRef = useRef<GenNode[] | null>(null)
  if (toc.length > 0 && expandedInitRef.current !== toc) {
    expandedInitRef.current = toc
    setExpanded(getInitialExpandedIds(toc))
  }

  // Fetch table data for preview/range selection
  const tablePath = data?.table_path || ''
  const tableJob = data?.job || job

  const tableData = useTableData({
    customer: (tablePath || data?.spec) ? customer : '',
    job: (tablePath || data?.spec) ? tableJob : '',
    table: tablePath,
    filter: '',
    spec: data?.spec,
  })

  // #968 Part D: one-shot smart chart-type suggestion. Fires only after an
  // in-editor table pick (never on open for existing widgets), only in graph
  // mode, and stands down the moment the user touches the dropdown.
  useEffect(() => {
    if (!suggestPendingRef.current) return
    if (!tableData.data || tableData.loading) return
    suggestPendingRef.current = false
    if (dataRef.current?.view_mode !== 'graph') return
    const suggested = suggestChartType(tableData.data)
    if (suggested !== dataRef.current?.chart_type) {
      onDataChangeRef.current({ ...dataRef.current!, chart_type: suggested })
    }
  }, [tableData.data, tableData.loading])

  const handleNodeSelectToggle = (itemId: string, node: GenNode, isSelected: boolean) => {
    if (!isSelected) return

    if (node.type !== 'Table') {
      setSelected(itemId)
      return
    }

    const filename = node.value1 || ''
    const folderPath = node.value2 || ''
    const fullPath = folderPath ? `${folderPath}/${filename}` : filename

    setSelected(itemId)
    suggestPendingRef.current = true
    // #1808: a NEW chart (its first table pick) starts in table order. An
    // existing chart keeps whatever sort_order it has, or none — the legacy
    // order — so published dashboards never move.
    const isNewWidget = !data?.table_path && !data?.spec
    onDataChange({
      ...(data || { job: treeJob, table_path: '', view_mode: 'table' }),
      job: treeJob,
      table_path: fullPath,
      ...(isNewWidget && !data?.sort_order ? { sort_order: 'table' as const } : {}),
    })
  }

  const handleNodeExpandToggle = (itemId: string, _node: GenNode, isExpanded: boolean) => {
    if (isExpanded) {
      setExpanded([...expanded, itemId])
    } else {
      setExpanded(expanded.filter(id => id !== itemId))
    }
  }

  const handleChartTypeChange = (e: any) => {
    if (!data) return
    suggestPendingRef.current = false
    const nextType = e.target.value
    const next: TableGraphWidgetData = { ...data, chart_type: nextType }
    // #1172: switching TO a one-column chart type collapses the column
    // selection to the first selected chartable column — the generator plots
    // one column, so the stored selection (and the dropdown) must say so
    // instead of claiming "6 Columns" while one renders.
    if (isSingleColumnChartType(nextType) && tableData.data) {
      const columnAxis = chartableAxis(extractColumnLabels(tableData.data), extractColumnTypes(tableData.data))
      const { rowPositions, columnPositions } = effectiveSelectionPositions(data.selection, tableData.data)
      const effectiveCols = columnPositions.length > 0 ? columnPositions : columnAxis.positions
      const keep = effectiveCols.find((p) => columnAxis.positions.includes(p))
      if (keep !== undefined && !(columnPositions.length === 1 && columnPositions[0] === keep)) {
        next.selection = buildPositionalSelection(
          rowPositions,
          [keep],
          data.selection?.switch_axis || false,
          tableData.data,
        )
      }
    }
    onDataChange(next)
  }

  const handleDataTypeChange = (e: any) => {
    if (!data) return
    onDataChange({ ...data, data_type: e.target.value })
  }

  // What a table with no explicit pick is currently displaying — read off the
  // loaded CBT properties, so the control reflects reality before the first
  // edit (#969 Obs 14). A CBT can already show several bands at once, which
  // is exactly why the table control became a set (#969 comment 1 item 2).
  const editorCells = tableData.data?.properties?.cells
  const derivedTableDataTypes: DataType[] = [
    editorCells?.frequencies ? 'freq' : undefined,
    editorCells?.columnPercents ? 'colPct' : undefined,
    editorCells?.rowPercents ? 'rowPct' : undefined,
  ].filter((band): band is DataType => band !== undefined)

  // The bands the table widget is showing: an explicit pick, else the stored
  // single value read as a one-element set, else what the CBT displays.
  const tableDataTypes: DataType[] = (() => {
    const stored = resolveTableDataTypes(data?.data_types, data?.data_type)
    if (stored.length > 0) return stored
    return derivedTableDataTypes.length > 0 ? derivedTableDataTypes : ['colPct']
  })()

  /**
   * Ticks or unticks one band (#969 comment 1 item 2). At least one band is
   * always on — the last remaining checkbox is disabled, so there is no path
   * to a table with nothing in its cells. Display level only: the source
   * table, its spec, and Carbon settings are never touched.
   */
  const handleTableDataTypeToggle = (band: DataType, checked: boolean) => {
    if (!data) return
    const next = checked
      ? [...tableDataTypes, band]
      : tableDataTypes.filter(b => b !== band)
    if (next.length === 0) return
    onDataChange({ ...data, data_types: resolveTableDataTypes(next, undefined) })
  }

  // Saving through the new editor also retires the legacy `range` — the
  // selection it was converted into now carries the same slice, and leaving
  // both would apply two filters.
  const handleSelectionChange = (selection: TableGraphSelection | undefined) => {
    if (!data) return
    const { range: _legacyRange, ...rest } = data
    onDataChange({ ...rest, selection })
  }

  // Both modes now share the positional Select Data model (#969 Obs 12/13);
  // table mode adds the structural-labels toggle and keeps `view_config` for
  // widths/freeze/labels (and legacy hide lists until the first edit).
  const editingTableView = (data?.view_mode || 'table') !== 'graph'
  const paneSectionDefs = (editingTableView ? TABLE_PANE_SECTIONS : GRAPH_PANE_SECTIONS)
    .map((id) => CONFIG_SECTION_DEFS[id])

  // One combined write: a table-mode Select Data edit can rewrite the
  // selection AND retire the legacy view_config blocklist in the same
  // gesture, and it also retires the legacy `range` like the chart path.
  const handleTableSelectDataChange = (
    selection: TableGraphSelection | undefined,
    viewConfig: TableViewConfig | undefined,
  ) => {
    if (!data) return
    const { range: _legacyRange, ...rest } = data
    onDataChange({ ...rest, selection, view_config: viewConfig })
  }

  const handleFreezeChange = (frozen: boolean) => {
    if (!data) return
    onDataChange({ ...data, view_config: { ...data.view_config, freeze_headers: frozen } })
  }

  // #969 Part D: a hide instruction naming a row the source no longer has is
  // moot, so it is cleaned silently — no dialog, no widget-face notice. The
  // dropped ids are reported for the Review Changes panel to log.
  //
  // A render-phase ref guard, not an effect: this must run once per (table,
  // config) pair and must not re-fire on unrelated re-renders. The write is
  // deferred to a microtask so it never lands during another component's
  // render.
  const cleanedForRef = useRef<string>('')
  const cleanupKey = `${tablePath}|${JSON.stringify(data?.view_config || {})}`
  if (tableData.data && data?.view_config && cleanedForRef.current !== cleanupKey) {
    cleanedForRef.current = cleanupKey
    const { config: cleaned, orphans } = cleanOrphanedReferences(data.view_config, tableData.data)
    if (orphans.length > 0 || cleaned !== data.view_config) {
      queueMicrotask(() => {
        const current = dataRef.current
        if (!current) return
        onDataChangeRef.current({ ...current, view_config: cleaned })
      })
    }
  }

  // Direct manipulation on the visual (#968 Part A): clicking a series in the
  // preview's legend drops it. One-way — the engine filters removed series out
  // of the data, so there is no legend item left to click again. The row list
  // above is where they go back.
  const handleSeriesRemove = (seriesName: string) => {
    if (!data || !tableData.data) return
    const current = effectiveSelection(data, tableData.data)
    const next = removeSeriesFromSelection(current, seriesName, tableData.data)
    if (next === current) return
    handleSelectionChange(next)
  }

  const handleAddColorOverride = () => {
    const name = newOverrideName.trim()
    if (!name || !data) return
    onDataChange({
      ...data,
      color_overrides: { ...data.color_overrides, [name]: newOverrideColor },
    })
    setNewOverrideName('')
    setNewOverrideColor('#4E79A7')
  }

  const handleDeleteColorOverride = (brandName: string) => {
    if (!data) return
    const next = { ...data.color_overrides }
    delete next[brandName]
    onDataChange({
      ...data,
      color_overrides: Object.keys(next).length > 0 ? next : undefined,
    })
  }

  // Tier 2 (#968 Part B). Saving in the overlay repoints the widget at the new
  // table, so the user returns to a chart already showing what they built.
  // Any selection is dropped: row and column names belong to the old table and
  // may not exist in the new one.
  const handleEditDataSaved = (savedPath: string) => {
    onDataChange({
      ...(data || { job: tableJob, view_mode: 'table' as const }),
      job: tableJob,
      table_path: savedPath,
      selection: undefined,
      range: undefined,
    })
  }

  // Chart design controls (#968 Part C): every property inherits the
  // design-system default until explicitly overridden here, and each
  // override has its own "revert to theme" affordance. See chartDesign.ts
  // for the resolution/override model this mirrors.
  const resolvedDesign = useMemo(
    () => resolveChartDesign(data?.design, data?.chart_type),
    [data?.design, data?.chart_type],
  )

  const handleDesignPropertyChange = (path: ChartDesignPropertyPath, value: string | number | boolean) => {
    if (!data) return
    onDataChange({ ...data, design: setChartDesignProperty(data.design, path, value) })
  }

  const handleDesignPropertyRevert = (path: ChartDesignPropertyPath) => {
    if (!data) return
    onDataChange({ ...data, design: revertChartDesignProperty(data.design, path) as ChartDesign | undefined })
  }

  const handleDesignRevertAll = () => {
    if (!data) return
    onDataChange({ ...data, design: revertAllChartDesign() })
  }

  // #1174: the title-format row carries ONE revert for the whole group
  // (Matt's decision) — a user who overrode two title properties reverts both
  // and sets one again, rather than four inline buttons fighting for space.
  const TITLE_DESIGN_PATHS: ChartDesignPropertyPath[] = [
    'title.align', 'title.fontSize', 'title.fontWeight', 'title.color',
  ]
  const titleGroupOverridden = TITLE_DESIGN_PATHS.some((p) =>
    isChartDesignPropertyOverridden(data?.design, p)
  )
  const handleTitleGroupRevert = () => {
    if (!data) return
    onDataChange({ ...data, design: revertChartDesignTitleGroup(data.design) })
  }

  // #1178: the value-axis scale group gets ONE Auto button — Auto is the
  // absence of an override (the state every chart starts in), so it maps
  // onto the existing revert machinery rather than an explicit 'auto' value.
  const VALUE_AXIS_SCALE_PATHS: ChartDesignPropertyPath[] = [
    'valueAxis.min', 'valueAxis.max', 'valueAxis.majorUnit', 'valueAxis.balancedZero',
  ]
  const valueAxisScaleOverridden = VALUE_AXIS_SCALE_PATHS.some((p) =>
    isChartDesignPropertyOverridden(data?.design, p)
  )
  const handleValueAxisAuto = () => {
    if (!data) return
    let next = data.design as ChartDesign | undefined
    for (const p of VALUE_AXIS_SCALE_PATHS) {
      next = revertChartDesignProperty(next, p) as ChartDesign | undefined
    }
    onDataChange({ ...data, design: next })
  }

  // #1174 (Matt, 1 Sep): every Chart Design group follows the title row's
  // one-revert-per-group model, so the same two helpers serve them all.
  const designGroupOverridden = (paths: ChartDesignPropertyPath[]) =>
    paths.some((p) => isChartDesignPropertyOverridden(data?.design, p))
  const handleDesignGroupRevert = (paths: ChartDesignPropertyPath[]) => {
    if (!data) return
    let next = data.design as ChartDesign | undefined
    for (const p of paths) {
      next = revertChartDesignProperty(next, p) as ChartDesign | undefined
    }
    onDataChange({ ...data, design: next })
  }
  const X_AXIS_PATHS: ChartDesignPropertyPath[] = ['xAxis.labelFontSize', 'xAxis.labelColor', 'xAxis.titleText', 'xAxis.titleColor']
  const Y_AXIS_PATHS: ChartDesignPropertyPath[] = ['yAxis.labelFontSize', 'yAxis.labelColor', 'yAxis.titleText', 'yAxis.titleColor']
  const CATEGORY_LABEL_PATHS: ChartDesignPropertyPath[] = ['categoryAxis.labelInterval', 'categoryAxis.rangeSlider']
  const GRIDLINE_PATHS: ChartDesignPropertyPath[] = ['gridlines.color', 'gridlines.width', 'gridlines.dashStyle']
  const LEGEND_PATHS: ChartDesignPropertyPath[] = ['legend.show', 'legend.position', 'legend.fontSize', 'legend.fontColor', 'legend.showSymbol']
  const DATA_LABEL_PATHS: ChartDesignPropertyPath[] = ['dataLabels.show', 'dataLabels.numberFormat']

  // Label interval (#1178): stored user-facing — absent = Auto, 1 = every
  // label, N = every Nth. The N-1 ECharts quirk stays in applyChartDesign.
  const labelIntervalOverride = resolvedDesign.xAxis.labelInterval
  const labelIntervalMode = labelIntervalOverride == null
    ? 'auto'
    : labelIntervalOverride <= 1 ? 'every' : 'nth'
  const isPercentChart = (data?.data_type || 'freq') !== 'freq'
  // #1808: balanced sizes the axis itself, so min/max are disabled while on
  // (bar family only — it is the only place the switch shows).
  const balancedAxisOn = isBarFamilyChartType(data?.chart_type || 'bar') && resolvedDesign.yAxis.balancedZero === true

  // Chart templates (#1050): "Save as chart template" captures ONLY chart
  // type + ChartDesign overrides + colour overrides off the current widget
  // (see chartTemplate.ts) — no table, no data, no title text. "Apply
  // template" replaces the widget's design/color_overrides with the stored
  // ones as fresh explicit overrides, confirming first if that would
  // overwrite design work already on the widget.
  const snackbar = useSnackbar()
  const { chartTemplates, createChartTemplate, createChartTemplateIsLoading } = useChartTemplates({ customer })

  // Depends only on the widget's own design/color_overrides, never on
  // templateNameInput — the save button can be disabled for either reason,
  // but this hint must only explain the "nothing to capture" one.
  const saveTemplateHasNothingToCapture = !chartTemplateHasContent(captureChartTemplate(data, 'x'))

  const handleOpenSaveTemplate = () => {
    setTemplateNameInput(data?.title || 'Chart template')
    setTemplateDescriptionInput('')
    setSaveTemplateOpen(true)
  }

  const handleConfirmSaveTemplate = async () => {
    const capture = captureChartTemplate(data, templateNameInput, templateDescriptionInput)
    if (!capture.name) return
    try {
      await createChartTemplate(capture)
      snackbar.success('Chart template saved')
      setSaveTemplateOpen(false)
    } catch {
      // useChartTemplates already surfaced the error via snackbar.
    }
  }

  const applyTemplateById = (id: string) => {
    const template = chartTemplates.find((t) => t.id === id)
    if (!data || !template) return
    onDataChange(applyChartTemplateToWidgetData(data, template))
  }

  const handleChooseTemplateToApply = (id: string) => {
    if (chartTemplateApplyWouldOverwrite(data)) {
      setPendingApplyTemplateId(id)
      setConfirmOverwriteOpen(true)
      return
    }
    applyTemplateById(id)
    setApplyTemplateOpen(false)
  }

  const handleConfirmOverwriteApply = () => {
    applyTemplateById(pendingApplyTemplateId)
    setConfirmOverwriteOpen(false)
    setApplyTemplateOpen(false)
    setPendingApplyTemplateId('')
  }

  // Table design controls (#969 Part C): same override/revert-to-theme model
  // as Chart Design above, mirrored in tableDesign.ts. Table view mode only.
  const resolvedTableDesign = useMemo(() => resolveTableDesign(data?.table_design), [data?.table_design])

  const handleTableDesignPropertyChange = (path: TableDesignPropertyPath, value: string | number | boolean) => {
    if (!data) return
    onDataChange({ ...data, table_design: setTableDesignProperty(data.table_design, path, value) })
  }

  const handleTableDesignPropertyRevert = (path: TableDesignPropertyPath) => {
    if (!data) return
    onDataChange({ ...data, table_design: revertTableDesignProperty(data.table_design, path) as TableDesign | undefined })
  }

  const handleTableDesignRevertAll = () => {
    if (!data) return
    onDataChange({ ...data, table_design: revertAllTableDesign(data.table_design) })
  }

  const conditionalFormatRules = data?.table_design?.conditional_formatting || []

  const handleAddConditionalFormatRule = (rule: TableConditionalFormatRule) => {
    if (!data) return
    onDataChange({ ...data, table_design: addConditionalFormatRule(data.table_design, rule) })
  }

  const handleUpdateConditionalFormatRule = (index: number, rule: TableConditionalFormatRule) => {
    if (!data) return
    onDataChange({ ...data, table_design: updateConditionalFormatRule(data.table_design, index, rule) })
  }

  const handleRemoveConditionalFormatRule = (index: number) => {
    if (!data) return
    onDataChange({ ...data, table_design: removeConditionalFormatRule(data.table_design, index) })
  }

  // Bar-family settings (#1808). Sort and bar labels are data-shape settings
  // on the widget (beside skip_zero_base); bar colours live in the design so
  // chart templates carry them.
  const isBarFamilyChart = data?.view_mode === 'graph' && isBarFamilyChartType(data?.chart_type || 'bar')
  // An existing chart with no sort_order shows the order it has always had;
  // only a change writes the field.
  const sortOrderShown: ChartSortOrder = data?.sort_order ?? legacySortOrder(data?.chart_type || 'bar')
  const barColorRules = data?.design?.bar_colors?.rules || []
  const barDefaultColor = data?.design?.bar_colors?.default_color

  const handleSortOrderChange = (order: ChartSortOrder) => {
    if (!data) return
    onDataChange({ ...data, sort_order: order })
  }

  const handleCategoryLabelChange = (mode: ChartCategoryLabelMode) => {
    if (!data) return
    onDataChange({ ...data, category_label: mode === 'row' ? undefined : mode })
  }

  const handleAddBarColorRule = (rule: TableConditionalFormatRule) => {
    if (!data) return
    onDataChange({ ...data, design: addBarColorRule(data.design, rule) })
  }

  const handleUpdateBarColorRule = (index: number, rule: TableConditionalFormatRule) => {
    if (!data) return
    onDataChange({ ...data, design: updateBarColorRule(data.design, index, rule) })
  }

  const handleRemoveBarColorRule = (index: number) => {
    if (!data) return
    onDataChange({ ...data, design: removeBarColorRule(data.design, index) })
  }

  const handleBarDefaultColorChange = (color: string | undefined) => {
    if (!data) return
    onDataChange({ ...data, design: setBarColorDefault(data.design, color) })
  }

  const hasContent = Boolean(data?.table_path) || Boolean(data?.spec)

  // The table tree is OPEN whenever the editor opens (#1240 A1, Matt: "when I
  // edit a chart I should have the select table pane open") and collapses to
  // a slim rail only when a table is picked in this session (#1218) — the
  // pick is the moment the tree has done its job. Not persisted.
  const [treeOpen, setTreeOpen] = useState(true)
  const lastTablePathRef = useRef(data?.table_path)
  useEffect(() => {
    // Collapse when a (different) table is picked — the trigger is the pick
    // itself, not any other data change.
    if (data?.table_path && data.table_path !== lastTablePathRef.current) {
      setTreeOpen(false)
    }
    lastTablePathRef.current = data?.table_path
  }, [data?.table_path])

  // #968 Obs 10.4 / #969 Obs 13: a FRESHLY configured widget starts with
  // only actual data rows/columns selected — Base-type vecs (totals/bases,
  // the "filled" rows) are deselected by default, in BOTH modes. "Fresh"
  // means the widget had no table_path and no spec when this editor mounted
  // (the new-widget flow, where the user picks a table in this session): a
  // SAVED widget with a table but no selection means "show everything", and
  // opening its editor must leave that state unchanged. Fires once per table
  // (the ref guard), so clearing back to "all" afterwards sticks.
  // A table widget that already carries a legacy view_config hide list is
  // left alone: it was configured under the old model.
  //
  // The same gate stamps the group-label toggles OFF (#969 comment 2 item 4).
  // The default belongs HERE rather than in the resolver for a concrete
  // reason: "off" means hidden, so a resolver-level default would silently
  // strip the group labels off every table widget already saved. Written at
  // first configuration, it reaches new widgets only, and every stored widget
  // keeps the appearance it was signed off with.
  const wasUnconfiguredAtMountRef = useRef(!data?.table_path && !data?.spec)
  const defaultedSelectionForRef = useRef<string>('')
  useEffect(() => {
    if (!wasUnconfiguredAtMountRef.current) return
    if (!tableData.data || tableData.loading) return
    const current = dataRef.current
    if (!current) return
    if (current.selection || current.range) return
    if (current.view_mode !== 'graph' &&
        (current.view_config?.hidden_rows?.length || current.view_config?.hidden_columns?.length)) return
    const key = current.table_path || JSON.stringify(current.spec || {})
    if (!key || defaultedSelectionForRef.current === key) return
    defaultedSelectionForRef.current = key
    const defaulted = defaultDataOnlySelection(tableData.data)
    const next: TableGraphWidgetData = { ...current }
    if (defaulted) next.selection = defaulted
    if (current.view_mode !== 'graph') {
      next.view_config = withDefaultGroupLabelVisibility(current.view_config)
    }
    if (next.selection !== current.selection || next.view_config !== current.view_config) {
      onDataChangeRef.current(next)
    }
  }, [tableData.data, tableData.loading])

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 400, position: 'relative' }}>
      {/* Left side: TOC tree for table selection. The old Table/Spec toggle
          is gone (#968 Obs 4): the user either selects an existing table here
          or creates one via Edit Data. Widgets that already carry an inline
          spec keep rendering it; they just aren't authored this way any more.
          #1218: once a table is chosen the tree collapses to a slim rail —
          the picker did its job, the chart is the work now. */}
      {treeOpen ? (
        <Paper
          variant="outlined"
          sx={{
            // 320, was 260 (#1176): long table names were cut off. The cheapest
            // of the three width options in the issue — flagged for Matt's
            // test; a draggable divider is the recorded upgrade path.
            width: 320,
            minWidth: 320,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* ONE header line (#1176 Surface 3); the job label is gone too
              (#1132 rule: we know what job we're in — a dashboard is
              single-job, and the editor never offers another). */}
          <Box sx={{ p: 1.5, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ flex: 1 }}>
              Select Table
            </Typography>
            {hasContent && (
              <Tooltip title="Collapse table list">
                <IconButton size="small" aria-label="Collapse table list" onClick={() => setTreeOpen(false)}>
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            <DashboardJobSelect value={treeJob} onChange={(j) => { setPickJob(j); setSelected('') }} />
            {tocLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Tree
                searchEnabled={true}
                searchPlaceholder="Search tables..."
                nodes={toc}
                expanded={expanded}
                selected={selected}
                onNodeSelectToggle={handleNodeSelectToggle}
                onNodeExpandToggle={handleNodeExpandToggle}
              />
            )}
          </Box>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ width: 44, minWidth: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
          <Tooltip title="Change table" placement="right">
            <IconButton size="small" aria-label="Change table" onClick={() => setTreeOpen(true)}>
              <TableRowsOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Paper>
      )}

      {/* Right side: Config + Preview */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {!hasContent ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <TableChartIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ mb: 2 }}>
              Select a table from the tree, or create a new one.
            </Typography>
            {/* Tier 2 (#968 Part B): the same Edit Data overlay, opened with a
                blank spec — tablePath is already '' here since hasContent is
                false. */}
            <Button
              size="small"
              color="info"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEditDataOpen(true)}
            >
              Create New
            </Button>
          </Box>
        ) : (
          <>
            {/* #968 Obs 8: configuration leads — a compact row above the
                preview: Display Title (half width) · Chart Type · Data Type.
                No "Leave blank to use default" hint.

                Round-2 review (#968 comment 2 / #969 item 1): the table
                source used to sit at the END of that row, where it truncated.
                It now has its own line ABOVE the boxes and wraps, so the whole
                path is readable. */}
            <Typography
              data-testid="widget-table-source"
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.5, wordBreak: 'break-word' }}
            >
              {data?.table_path
                ? <>Table: <strong>{data.table_path}</strong> ({jobLabel(data.job || job)})</>
                : <>Spec: <strong>{(data?.spec?.top as string) || '?'} × {(data?.spec?.side as string) || '?'}</strong> ({jobLabel(data?.job || job)})</>
              }
            </Typography>
            {/* pt: 1 is the headroom the outlined labels ("Display Title",
                "Chart Type", "Data Type") need: they sit above the field's
                border box, and with no top padding the row clipped them. */}
            <Box
              data-testid="widget-config-row"
              sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1, mb: 2, flexWrap: 'wrap' }}
            >
              {(() => {
                const tableName = data?.table_path?.split('/').pop()?.replace(/\.[^.]+$/, '') || ''
                const sideLabel = tableData.data?.meta?.side
                const placeholder = sideLabel ? `${tableName} — ${sideLabel}` : tableName
                return (
                  <TextField
                    size="small"
                    label="Display Title"
                    value={data?.title ?? ''}
                    placeholder={placeholder}
                    onChange={(e) => {
                      if (!data) return
                      const val = e.target.value
                      onDataChange({ ...data, title: val || undefined })
                    }}
                    sx={{ flexBasis: '50%', flexShrink: 0 }}
                  />
                )
              })()}
              {/* Chart Type and Data Type moved into the configuration
                  pane's Data section (#1218) — the row above the preview is
                  the TITLE row (Matt's answer to Q1), nothing else. */}
            </Box>

            {/* #1174: the title FORMAT controls live in one row with the
                title field above and the Preview below, so the user types,
                formats and sees the result in the same view — they no longer
                hide at the bottom of the Chart Design collapse. One revert
                for the whole group (Matt's decision); the stored per-property
                model in chartDesign.ts is unchanged. */}
            {data?.view_mode === 'graph' && (
              <Box
                data-testid="title-format-row"
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={resolvedDesign.title.align}
                  onChange={(_e, v) => { if (v) handleDesignPropertyChange('title.align', v) }}
                >
                  <ToggleButton value="left" aria-label="Align title left">
                    <FormatAlignLeftIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="center" aria-label="Align title center">
                    <FormatAlignCenterIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="right" aria-label="Align title right">
                    <FormatAlignRightIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  type="number"
                  value={resolvedDesign.title.fontSize}
                  onChange={(e) => handleDesignPropertyChange('title.fontSize', Number(e.target.value))}
                  sx={{ width: 96 }}
                  InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                  inputProps={{ min: 8, max: 48, 'aria-label': 'Title font size' }}
                />
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <Select
                    value={resolvedDesign.title.fontWeight}
                    onChange={(e) => handleDesignPropertyChange('title.fontWeight', Number(e.target.value))}
                    inputProps={{ 'aria-label': 'Title font weight' }}
                  >
                    <MenuItem value={400}>Regular</MenuItem>
                    <MenuItem value={500}>Medium</MenuItem>
                    <MenuItem value={600}>Semibold</MenuItem>
                    <MenuItem value={700}>Bold</MenuItem>
                    <MenuItem value={800}>Extra bold</MenuItem>
                  </Select>
                </FormControl>
                {/* The word "Color" is no longer beside the swatch — the
                    tooltip carries it. */}
                <Tooltip title="Title colour">
                  <input
                    type="color"
                    aria-label="Title colour"
                    value={resolvedDesign.title.color}
                    onChange={(e) => handleDesignPropertyChange('title.color', e.target.value)}
                    style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                  />
                </Tooltip>
                <Tooltip title="Revert title style to theme">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Revert title style"
                      onClick={handleTitleGroupRevert}
                      disabled={!titleGroupOverridden}
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
              <TableGraphWidgetRenderer
                data={data!}
                customer={customer}
                job={tableJob}
                colorMap={colorMap}
                themeColorMaps={themeColorMaps}
                onSeriesRemove={handleSeriesRemove}
              />
            </Box>
            {/* #968 Obs 5: Edit Data is the Tier 2 escape hatch — a solid
                blue button, bottom LEFT of the preview panel. */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Button
                size="small"
                color="info"
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditDataOpen(true)}
              >
                Edit Data
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* #1218: the configuration pane — everything that is not the preview,
          in collapsible sections with their OWN scroll bar (Matt: never make
          the user scroll the whole modal to reach a setting). Collapsed to an
          icon rail until asked for; on narrow viewports the open pane
          overlays the preview instead of squeezing it. */}
      {hasContent && (
        <Paper
          variant="outlined"
          sx={{
            width: paneState.open ? 360 : 44,
            minWidth: paneState.open ? 360 : 44,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(paneOverlays && paneState.open
              ? { position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 10, boxShadow: 8 }
              : {}),
          }}
        >
          {!paneState.open ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1, gap: 0.5 }}>
              <Tooltip title="Open configuration" placement="left">
                <IconButton size="small" aria-label="Open configuration" onClick={() => setPane(true, paneState.sections)}>
                  <ChevronLeftIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Divider flexItem sx={{ my: 0.5 }} />
              {paneSectionDefs.map((def) => (
                <Tooltip key={def.id} title={def.title} placement="left">
                  <IconButton size="small" aria-label={`${def.title} settings`} onClick={() => openPaneAt(def.id)}>
                    <Box sx={{ display: 'flex', color: 'text.secondary', '& svg': { fontSize: 20 } }}>{def.icon}</Box>
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>Configuration</Typography>
                <Tooltip title="Collapse configuration">
                  <IconButton size="small" aria-label="Collapse configuration" onClick={() => setPane(false, paneState.sections)}>
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box data-testid="config-pane-scroll" sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <ConfigSection
                  def={CONFIG_SECTION_DEFS.data}
                  open={paneState.sections.includes('data')}
                  onToggle={() => togglePaneSection('data')}
                >
                  {data?.view_mode === 'graph' && (
                    <FormControl size="small" fullWidth>
                      <InputLabel id={chartTypeLabelId}>Chart Type</InputLabel>
                      <Select
                        labelId={chartTypeLabelId}
                        value={data?.chart_type || 'bar'}
                        onChange={handleChartTypeChange}
                        label="Chart Type"
                      >
                        {(Object.keys(CHART_TYPE_LABELS) as ChartType[])
                          .filter(ct => ct !== 'table')
                          .map((ct) => (
                            <MenuItem key={ct} value={ct}>
                              {CHART_TYPE_LABELS[ct]}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                  {/* Data Type is offered in BOTH modes (#969 Obs 14), but not in
                      the same shape. A CHART plots one measure, so it keeps the
                      single-select dropdown. A TABLE can show Frequencies,
                      Column % and Row % in the same cell stack, so it gets
                      checkboxes (#969 comment 1 item 2) with at least one always
                      on. Either way it is DISPLAY-level only — it changes which
                      bands the widget renders, never the source table's spec or
                      Carbon settings. A table with no explicit pick shows the
                      bands its CBT properties already display, so existing
                      widgets are unchanged. */}
                  {data?.view_mode === 'graph' ? (
                    <FormControl size="small" fullWidth>
                      <InputLabel id={dataTypeLabelId}>Data Type</InputLabel>
                      <Select
                        labelId={dataTypeLabelId}
                        value={data?.data_type || 'freq'}
                        onChange={handleDataTypeChange}
                        label="Data Type"
                      >
                        {DATA_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Data Type
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {DATA_TYPE_OPTIONS.map((opt) => {
                          const checked = tableDataTypes.includes(opt.value)
                          return (
                            <FormControlLabel
                              key={opt.value}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  // The last band on cannot be turned off.
                                  disabled={checked && tableDataTypes.length === 1}
                                  onChange={(e) =>
                                    handleTableDataTypeToggle(opt.value, e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">{opt.label}</Typography>}
                              sx={{ mr: 0 }}
                            />
                          )
                        })}
                      </Box>
                    </Box>
                  )}
                  {/* #968 Obs 10 / #969 Obs 13: Select Data — which rows/columns
                      the widget shows, as flat positional dropdowns in BOTH modes.
                      Table mode lists top-level row labels (no hierarchy — the old
                      nested checkbox tree exposed Carbon's internal structure) and
                      adds the structural-labels toggle (Obs 15). */}
                  <Typography variant="caption" fontWeight={600}>
                    Select Data
                  </Typography>
                  {tableData.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tableData.isError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load table data for row and column selection
              </Alert>
            ) : tableData.data ? (
              editingTableView ? (
                <TableSelectDataPanel
                  data={tableData.data}
                  selection={effectiveSelection(data || {}, tableData.data)}
                  viewConfig={data?.view_config}
                  onChange={handleTableSelectDataChange}
                />
              ) : (
                <TableSelectionEditor
                  data={tableData.data}
                  selection={effectiveSelection(data || {}, tableData.data)}
                  onChange={handleSelectionChange}
                  singleColumn={data?.view_mode === 'graph' && isSingleColumnChartType(data?.chart_type)}
                />
              )
            ) : null}
                  {/* #1181 — a data-shape setting, not a themed design
                      property, so it lives in the Data section (2 Sep call):
                      outside the ChartDesign override/revert model, stored as
                      skip_zero_base on the widget. Default ON: only an
                      explicit false turns it off, so charts saved before the
                      field existed pick it up too. */}
                  {data?.view_mode === 'graph' && (
                    <FormControlLabel
                      control={
                        <Tooltip title="When the base (Cases WF) is 0 there is no data for that period — leave a gap instead of plotting a zero. The line then starts at the first real data point.">
                          <Switch
                            size="small"
                            checked={data?.skip_zero_base !== false}
                            onChange={(e) => {
                              if (!data) return
                              onDataChange({ ...data, skip_zero_base: e.target.checked ? undefined : false })
                            }}
                          />
                        </Tooltip>
                      }
                      label={<Typography variant="body2">Skip zero-base points</Typography>}
                      sx={{ ml: 0 }}
                    />
                  )}
                  {/* #1808 — bar order and bar labels: data-shape settings
                      for the bar family, stored on the widget like
                      skip_zero_base. */}
                  {isBarFamilyChart && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id={sortOrderLabelId}>Sort</InputLabel>
                        <Select
                          labelId={sortOrderLabelId}
                          label="Sort"
                          value={sortOrderShown}
                          inputProps={{ 'aria-label': 'Sort' }}
                          onChange={(e) => handleSortOrderChange(e.target.value as ChartSortOrder)}
                        >
                          <MenuItem value="table">Table order</MenuItem>
                          <MenuItem value="desc">Highest first</MenuItem>
                          <MenuItem value="asc">Lowest first</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id={categoryLabelLabelId}>Bar labels</InputLabel>
                        <Select
                          labelId={categoryLabelLabelId}
                          label="Bar labels"
                          value={data?.category_label || 'row'}
                          inputProps={{ 'aria-label': 'Bar labels' }}
                          onChange={(e) => handleCategoryLabelChange(e.target.value as ChartCategoryLabelMode)}
                        >
                          <MenuItem value="row">Row label</MenuItem>
                          <MenuItem value="group_row">Group heading + row label</MenuItem>
                          <MenuItem value="group">Group heading only</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </ConfigSection>

                {/* Theme (#1193/#1175) — the theme sets the colours, and the
                    overrides below it are this chart's own changes on top. The
                    chart stores the theme ID (a live reference, like the
                    page) — never the colours. */}
                {data?.view_mode === 'graph' && (
                  <ConfigSection
                    def={CONFIG_SECTION_DEFS.theme}
                    open={paneState.sections.includes('theme')}
                    onToggle={() => togglePaneSection('theme')}
                  >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {pageThemeName && (
                  <Typography variant="caption" color="text.secondary">
                    Page theme:{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        const theme = themes.find((t) => t.name === pageThemeName)
                        if (theme) setThemePeek({ anchor: e.currentTarget, theme })
                      }}
                      sx={{ verticalAlign: 'baseline' }}
                    >
                      {pageThemeName}
                    </Link>
                  </Typography>
                )}
                <FormControl size="small" sx={{ maxWidth: 320 }}>
                  {/* shrink + notched, explicitly (#1172 request 5): with
                      displayEmpty the value '' renders visible text ("Page
                      theme (…)"), but MUI's shrink logic keys off the VALUE —
                      so the un-shrunk label painted on top of it ("jumbled
                      text on first view") until any interaction shrank it. */}
                  <InputLabel id={chartThemeLabelId} shrink>Chart theme</InputLabel>
                  <Select
                    labelId={chartThemeLabelId}
                    value={data?.brand_palette_key && themes.some((t) => t.id === data.brand_palette_key) ? data.brand_palette_key : ''}
                    input={<OutlinedInput notched label="Chart theme" />}
                    displayEmpty
                    onChange={(e) => {
                      if (!data) return
                      const v = e.target.value
                      onDataChange({ ...data, brand_palette_key: v || undefined })
                    }}
                  >
                    <MenuItem value="">
                      <em>{pageThemeName ? `Page theme (${pageThemeName})` : 'Page default'}</em>
                    </MenuItem>
                    {themes.map((theme) => (
                      <MenuItem key={theme.id} value={theme.id}>
                        <ListItemText
                          primary={theme.name}
                          secondary={`${Object.keys(theme.palette).length} colours`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {Boolean(data?.brand_palette_key) && !themes.some((t) => t.id === data?.brand_palette_key) && (
                  <Typography variant="caption" color="warning.main">
                    This chart's theme no longer exists — the page colours are showing.
                  </Typography>
                )}
                <Popover
                  open={Boolean(themePeek)}
                  anchorEl={themePeek?.anchor}
                  onClose={() => setThemePeek(null)}
                >
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 160 }}>
                    <Typography variant="subtitle2">{themePeek?.theme.name}</Typography>
                    {Object.entries(themePeek?.theme.palette || {}).map(([brand, entry]) => (
                      <Box key={brand} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
                        <Typography variant="body2">{brand}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Popover>
              </Box>

              {/* Colour Overrides */}
              <Box>
                <Button
                  size="small"
                  onClick={() => setColorOverridesOpen(!colorOverridesOpen)}
                  endIcon={colorOverridesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', color: 'text.secondary', p: 0, mb: 0.5 }}
                >
                  Colour Overrides ({Object.keys(data?.color_overrides || {}).length})
                </Button>
                <Collapse in={colorOverridesOpen}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    {Object.entries(data?.color_overrides || {}).map(([brandName, color]) => (
                      <Box key={brandName} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => {
                            if (!data) return
                            onDataChange({
                              ...data,
                              color_overrides: { ...data.color_overrides, [brandName]: e.target.value },
                            })
                          }}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>{brandName}</Typography>
                        <IconButton size="small" onClick={() => handleDeleteColorOverride(brandName)}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="color"
                        value={newOverrideColor}
                        onChange={(e) => setNewOverrideColor(e.target.value)}
                        style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                      />
                      {/* #1174 item 2: the field takes a TEXT MATCH against
                          series labels, not a brand name — say so. The
                          renderer lowercases and trims the keys, so the match
                          ignores case and surrounding spaces. */}
                      <TextField
                        size="small"
                        placeholder="Text match, e.g. brand name"
                        value={newOverrideName}
                        onChange={(e) => setNewOverrideName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddColorOverride() }}
                        sx={{ flex: 1 }}
                      />
                      <Tooltip title="The colour applies to any series whose label matches the text you type here. Matching ignores case and spaces around the label.">
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      </Tooltip>
                      <IconButton size="small" onClick={handleAddColorOverride} disabled={!newOverrideName.trim()} color="info">
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {/* Bar colours by value (#1808) — the table widget's rule
                  model on bars. Rules, then the default, win over the
                  colour overrides above. */}
              {isBarFamilyChart && (
                <Box data-testid="bar-colors-group">
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                    Bar colours by value
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    First matching rule wins. On a percentage chart, 5 means 5%. Rules beat colour overrides.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <input
                      type="color"
                      aria-label="Default bar colour"
                      value={barDefaultColor || '#BAB0AC'}
                      onChange={(e) => handleBarDefaultColorChange(e.target.value)}
                      style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {barDefaultColor ? 'Default colour (no rule matches)' : 'Default colour: none — unmatched bars keep their colour'}
                    </Typography>
                    {barDefaultColor && (
                      <Button size="small" color="info" onClick={() => handleBarDefaultColorChange(undefined)} sx={{ textTransform: 'none', py: 0 }}>
                        Clear
                      </Button>
                    )}
                  </Box>
                  <ConditionalFormatRuleList
                    rules={barColorRules}
                    onAdd={handleAddBarColorRule}
                    onUpdate={handleUpdateBarColorRule}
                    onRemove={handleRemoveBarColorRule}
                    colorTitle="Bar colour"
                    initialThreshold="5"
                  />
                </Box>
              )}
                  </ConfigSection>
                )}

            {/* Chart Design (#968 Part C), re-housed as pane sections (#1218,
                2 Sep call): Axis, Labels, Legend, Gridlines. Every control
                defaults to the design-system theme value; editing one stores
                an explicit override, with the #1174 group treatment — icon +
                name header, ONE revert per group, controls in a compact
                wrapping row. */}
            {data?.view_mode === 'graph' && (
              <>
                <ConfigSection
                  def={CONFIG_SECTION_DEFS.axis}
                  open={paneState.sections.includes('axis')}
                  onToggle={() => togglePaneSection('axis')}
                >
                    <DesignGroup
                      icon={<SwapHorizIcon />}
                      title="X Axis"
                      overridden={designGroupOverridden(X_AXIS_PATHS)}
                      onRevert={() => handleDesignGroupRevert(X_AXIS_PATHS)}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Label size"
                        InputLabelProps={{ shrink: true }}
                        value={resolvedDesign.xAxis.labelFontSize}
                        onChange={(e) => handleDesignPropertyChange('xAxis.labelFontSize', Number(e.target.value))}
                        sx={{ width: 96 }}
                        inputProps={{ min: 6, max: 40, 'aria-label': 'X axis label size' }}
                      />
                      <Tooltip title="Label colour">
                        <input
                          type="color"
                          aria-label="X axis label colour"
                          value={resolvedDesign.xAxis.labelColor}
                          onChange={(e) => handleDesignPropertyChange('xAxis.labelColor', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                      <TextField
                        size="small"
                        label="Title"
                        InputLabelProps={{ shrink: true }}
                        placeholder="(none)"
                        value={resolvedDesign.xAxis.titleText}
                        onChange={(e) => handleDesignPropertyChange('xAxis.titleText', e.target.value)}
                        sx={{ width: 180 }}
                        inputProps={{ 'aria-label': 'X axis title text' }}
                      />
                      <Tooltip title="Title colour">
                        <input
                          type="color"
                          aria-label="X axis title colour"
                          value={resolvedDesign.xAxis.titleColor}
                          onChange={(e) => handleDesignPropertyChange('xAxis.titleColor', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                    </DesignGroup>

                    <DesignGroup
                      icon={<SwapVertIcon />}
                      title="Y Axis"
                      overridden={designGroupOverridden(Y_AXIS_PATHS)}
                      onRevert={() => handleDesignGroupRevert(Y_AXIS_PATHS)}
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Label size"
                        InputLabelProps={{ shrink: true }}
                        value={resolvedDesign.yAxis.labelFontSize}
                        onChange={(e) => handleDesignPropertyChange('yAxis.labelFontSize', Number(e.target.value))}
                        sx={{ width: 96 }}
                        inputProps={{ min: 6, max: 40, 'aria-label': 'Y axis label size' }}
                      />
                      <Tooltip title="Label colour">
                        <input
                          type="color"
                          aria-label="Y axis label colour"
                          value={resolvedDesign.yAxis.labelColor}
                          onChange={(e) => handleDesignPropertyChange('yAxis.labelColor', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                      <TextField
                        size="small"
                        label="Title"
                        InputLabelProps={{ shrink: true }}
                        placeholder="(none)"
                        value={resolvedDesign.yAxis.titleText}
                        onChange={(e) => handleDesignPropertyChange('yAxis.titleText', e.target.value)}
                        sx={{ width: 180 }}
                        inputProps={{ 'aria-label': 'Y axis title text' }}
                      />
                      <Tooltip title="Title colour">
                        <input
                          type="color"
                          aria-label="Y axis title colour"
                          value={resolvedDesign.yAxis.titleColor}
                          onChange={(e) => handleDesignPropertyChange('yAxis.titleColor', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                    </DesignGroup>

                    {/* Value axis scale (#1178) — targets whichever axis is
                        the VALUE axis (applyChartDesign routes by the built
                        option's axis type). Keeps its Matt-approved Auto
                        button as the group revert. */}
                    <DesignGroup
                      icon={<StraightenIcon />}
                      title="Value Axis Scale"
                      testId="value-axis-scale-group"
                      overridden={valueAxisScaleOverridden}
                      onRevert={handleValueAxisAuto}
                      revertControl={
                        <Button
                          size="small"
                          color="info"
                          disabled={!valueAxisScaleOverridden}
                          onClick={handleValueAxisAuto}
                          sx={{ textTransform: 'none', py: 0 }}
                        >
                          Auto
                        </Button>
                      }
                    >
                      {isBarFamilyChart && (
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={resolvedDesign.yAxis.balancedZero === true}
                              inputProps={{ 'aria-label': 'Balanced around 0' }}
                              onChange={(e) => e.target.checked
                                ? handleDesignPropertyChange('valueAxis.balancedZero', true)
                                : handleDesignPropertyRevert('valueAxis.balancedZero')}
                            />
                          }
                          label={
                            <Tooltip title="Zero in the centre. The axis runs from minus to plus the longest bar, plus half a point, rounded up — recalculated every time the chart draws.">
                              <Typography variant="body2">Balanced around 0</Typography>
                            </Tooltip>
                          }
                          sx={{ ml: 0 }}
                        />
                      )}
                      <NumericDesignField
                        label="Minimum"
                        value={resolvedDesign.yAxis.min}
                        ariaLabel="Value axis minimum"
                        disabled={balancedAxisOn}
                        validate={(v) =>
                          resolvedDesign.yAxis.max != null && v >= resolvedDesign.yAxis.max
                            ? 'Must be below the maximum'
                            : null}
                        onCommit={(v) => v == null
                          ? handleDesignPropertyRevert('valueAxis.min')
                          : handleDesignPropertyChange('valueAxis.min', v)}
                      />
                      <NumericDesignField
                        label="Maximum"
                        value={resolvedDesign.yAxis.max}
                        ariaLabel="Value axis maximum"
                        disabled={balancedAxisOn}
                        validate={(v) =>
                          // Only an explicit minimum bounds it: since #1808
                          // the value axis has no 0 floor, so a negative
                          // maximum is a legitimate choice.
                          resolvedDesign.yAxis.min != null && v <= resolvedDesign.yAxis.min
                            ? 'Must be above the minimum'
                            : null}
                        onCommit={(v) => v == null
                          ? handleDesignPropertyRevert('valueAxis.max')
                          : handleDesignPropertyChange('valueAxis.max', v)}
                      />
                      <NumericDesignField
                        label="Major unit"
                        value={resolvedDesign.yAxis.majorUnit}
                        ariaLabel="Value axis major unit"
                        validate={(v) => (v <= 0 ? 'Must be above zero' : null)}
                        onCommit={(v) => v == null
                          ? handleDesignPropertyRevert('valueAxis.majorUnit')
                          : handleDesignPropertyChange('valueAxis.majorUnit', v)}
                      />
                      {isPercentChart && (
                        <Tooltip title="Percent data is stored as fractions, so type the number the data is in: 1 = 100%, 0.5 = 50%, 0 = 0%. A 0–100% axis is minimum 0, maximum 1.">
                          <Typography variant="caption" color="text.secondary">
                            1 = 100%, 0.5 = 50%, 0 = 0%
                          </Typography>
                        </Tooltip>
                      )}
                    </DesignGroup>
                </ConfigSection>

                <ConfigSection
                  def={CONFIG_SECTION_DEFS.labels}
                  open={paneState.sections.includes('labels')}
                  onToggle={() => togglePaneSection('labels')}
                >
                    {/* Category labels (#1178 Part 2) — thins the LABELS
                        only; every data point still plots. Range slider
                        (#1172) is the line chart's old dataZoom, opt-in. */}
                    <DesignGroup
                      icon={<LabelOutlinedIcon />}
                      title="Category Labels"
                      testId="category-labels-group"
                      overridden={designGroupOverridden(CATEGORY_LABEL_PATHS)}
                      onRevert={() => handleDesignGroupRevert(CATEGORY_LABEL_PATHS)}
                    >
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel shrink id={labelIntervalLabelId}>Label interval</InputLabel>
                        <Select
                          labelId={labelIntervalLabelId}
                          value={labelIntervalMode}
                          input={<OutlinedInput notched label="Label interval" />}
                          inputProps={{ 'aria-label': 'Category label interval' }}
                          onChange={(e) => {
                            const mode = e.target.value
                            if (mode === 'auto') handleDesignPropertyRevert('categoryAxis.labelInterval')
                            else if (mode === 'every') handleDesignPropertyChange('categoryAxis.labelInterval', 1)
                            else handleDesignPropertyChange('categoryAxis.labelInterval', 2)
                          }}
                        >
                          <MenuItem value="auto">Auto</MenuItem>
                          <MenuItem value="every">Every label</MenuItem>
                          <MenuItem value="nth">Every Nth</MenuItem>
                        </Select>
                      </FormControl>
                      {labelIntervalMode === 'nth' && (
                        <TextField
                          size="small"
                          type="number"
                          value={labelIntervalOverride}
                          onChange={(e) => {
                            const n = Math.floor(Number(e.target.value))
                            // The user types N and MEANS "every Nth" —
                            // the ECharts N-1 quirk never surfaces here.
                            if (Number.isFinite(n) && n >= 2) {
                              handleDesignPropertyChange('categoryAxis.labelInterval', n)
                            }
                          }}
                          sx={{ width: 80 }}
                          inputProps={{ min: 2, 'aria-label': 'Show every Nth label' }}
                        />
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={data?.design?.x_axis?.range_slider === true}
                            inputProps={{ 'aria-label': 'Show range slider' }}
                            onChange={(e) => {
                              if (e.target.checked) handleDesignPropertyChange('categoryAxis.rangeSlider', true)
                              else handleDesignPropertyRevert('categoryAxis.rangeSlider')
                            }}
                          />
                        }
                        label={<Typography variant="body2">Range slider</Typography>}
                        sx={{ ml: 0 }}
                      />
                    </DesignGroup>

                    <DesignGroup
                      icon={<NumbersIcon />}
                      title="Data Labels"
                      overridden={designGroupOverridden(DATA_LABEL_PATHS)}
                      onRevert={() => handleDesignGroupRevert(DATA_LABEL_PATHS)}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={resolvedDesign.dataLabels.show}
                            inputProps={{ 'aria-label': 'Show data labels' }}
                            onChange={(e) => handleDesignPropertyChange('dataLabels.show', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Show</Typography>}
                        sx={{ ml: 0 }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel shrink id={numberFormatLabelId}>Number format</InputLabel>
                        <Select
                          labelId={numberFormatLabelId}
                          value={resolvedDesign.dataLabels.numberFormat}
                          input={<OutlinedInput notched label="Number format" />}
                          onChange={(e) => handleDesignPropertyChange('dataLabels.numberFormat', e.target.value)}
                        >
                          <MenuItem value="auto">Auto (match Data Type)</MenuItem>
                          <MenuItem value="integer">Number</MenuItem>
                          <MenuItem value="percent0">Percent (0dp)</MenuItem>
                          <MenuItem value="percent1">Percent (1dp)</MenuItem>
                          <MenuItem value="percent2">Percent (2dp)</MenuItem>
                        </Select>
                      </FormControl>
                    </DesignGroup>
                </ConfigSection>

                <ConfigSection
                  def={CONFIG_SECTION_DEFS.legend}
                  open={paneState.sections.includes('legend')}
                  onToggle={() => togglePaneSection('legend')}
                >
                    <DesignGroup
                      icon={<LegendToggleIcon />}
                      title="Legend"
                      overridden={designGroupOverridden(LEGEND_PATHS)}
                      onRevert={() => handleDesignGroupRevert(LEGEND_PATHS)}
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={resolvedDesign.legend.show}
                            inputProps={{ 'aria-label': 'Show legend' }}
                            onChange={(e) => handleDesignPropertyChange('legend.show', e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Show</Typography>}
                        sx={{ ml: 0 }}
                      />
                      <Tooltip title="The coloured square before each name. Turn it off when the colours mean nothing, as on bars coloured by value.">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={resolvedDesign.legend.showSymbol}
                              inputProps={{ 'aria-label': 'Show legend symbol' }}
                              onChange={(e) => handleDesignPropertyChange('legend.showSymbol', e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Symbol</Typography>}
                          sx={{ ml: 0 }}
                        />
                      </Tooltip>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel shrink id={legendPositionLabelId}>Position</InputLabel>
                        <Select
                          labelId={legendPositionLabelId}
                          value={resolvedDesign.legend.position}
                          input={<OutlinedInput notched label="Position" />}
                          onChange={(e) => handleDesignPropertyChange('legend.position', e.target.value)}
                        >
                          <MenuItem value="top">Top</MenuItem>
                          <MenuItem value="bottom">Bottom</MenuItem>
                          <MenuItem value="left">Left</MenuItem>
                          <MenuItem value="right">Right</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        label="Font size"
                        InputLabelProps={{ shrink: true }}
                        value={resolvedDesign.legend.fontSize}
                        onChange={(e) => handleDesignPropertyChange('legend.fontSize', Number(e.target.value))}
                        sx={{ width: 96 }}
                        inputProps={{ min: 6, max: 40, 'aria-label': 'Legend font size' }}
                      />
                      <Tooltip title="Font colour">
                        <input
                          type="color"
                          aria-label="Legend font colour"
                          value={resolvedDesign.legend.fontColor}
                          onChange={(e) => handleDesignPropertyChange('legend.fontColor', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                    </DesignGroup>
                </ConfigSection>

                <ConfigSection
                  def={CONFIG_SECTION_DEFS.gridlines}
                  open={paneState.sections.includes('gridlines')}
                  onToggle={() => togglePaneSection('gridlines')}
                >
                    <DesignGroup
                      icon={<GridOnIcon />}
                      title="Gridlines"
                      overridden={designGroupOverridden(GRIDLINE_PATHS)}
                      onRevert={() => handleDesignGroupRevert(GRIDLINE_PATHS)}
                    >
                      <Tooltip title="Gridline colour">
                        <input
                          type="color"
                          aria-label="Gridline colour"
                          value={resolvedDesign.gridlines.color}
                          onChange={(e) => handleDesignPropertyChange('gridlines.color', e.target.value)}
                          style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                        />
                      </Tooltip>
                      <TextField
                        size="small"
                        type="number"
                        label="Width"
                        InputLabelProps={{ shrink: true }}
                        value={resolvedDesign.gridlines.width}
                        onChange={(e) => handleDesignPropertyChange('gridlines.width', Number(e.target.value))}
                        sx={{ width: 90 }}
                        inputProps={{ min: 0, max: 10, 'aria-label': 'Gridline width' }}
                      />
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel shrink id={dashStyleLabelId}>Dash style</InputLabel>
                        <Select
                          labelId={dashStyleLabelId}
                          value={resolvedDesign.gridlines.dashStyle}
                          input={<OutlinedInput notched label="Dash style" />}
                          onChange={(e) => handleDesignPropertyChange('gridlines.dashStyle', e.target.value)}
                        >
                          <MenuItem value="solid">Solid</MenuItem>
                          <MenuItem value="dashed">Dashed</MenuItem>
                          <MenuItem value="dotted">Dotted</MenuItem>
                        </Select>
                      </FormControl>
                    </DesignGroup>
                </ConfigSection>
              </>
            )}

            {/* Table mode sections (#1218): Styling (freeze) and Design
                (#969 Part C — header/cell styling, conditional formatting,
                significance), mirroring the chart sections' per-property
                "revert to theme" affordances. */}
            {data?.view_mode !== 'graph' && (
              <>
                <ConfigSection
                  def={CONFIG_SECTION_DEFS.styling}
                  open={paneState.sections.includes('styling')}
                  onToggle={() => togglePaneSection('styling')}
                >
                  <FormControlLabel
                    control={
                      <Tooltip title="Keeps the row labels visible when scrolling sideways, and the column headers when scrolling down.">
                        <Switch
                          size="small"
                          checked={isFrozen(data?.view_config)}
                          onChange={(e) => handleFreezeChange(e.target.checked)}
                        />
                      </Tooltip>
                    }
                    label={<Typography variant="body2">Freeze row labels and column headers</Typography>}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </ConfigSection>

                <ConfigSection
                  def={CONFIG_SECTION_DEFS.design}
                  open={paneState.sections.includes('design')}
                  onToggle={() => togglePaneSection('design')}
                >
                  {data?.table_design && (
                    <Button
                      size="small"
                      color="info"
                      onClick={handleTableDesignRevertAll}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start', p: 0 }}
                    >
                      Revert all to theme
                    </Button>
                  )}
                    <Box>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Group headers
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <DesignControlRow
                          label="Background"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.group.background_color')}
                          onRevert={() => handleTableDesignPropertyRevert('header.group.background_color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.header.group.backgroundColor}
                            onChange={(e) => handleTableDesignPropertyChange('header.group.background_color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Text color"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.group.text_color')}
                          onRevert={() => handleTableDesignPropertyRevert('header.group.text_color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.header.group.textColor}
                            onChange={(e) => handleTableDesignPropertyChange('header.group.text_color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Font weight"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.group.font_weight')}
                          onRevert={() => handleTableDesignPropertyRevert('header.group.font_weight')}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={resolvedTableDesign.header.group.fontWeight}
                            onChange={(e) => handleTableDesignPropertyChange('header.group.font_weight', Number(e.target.value))}
                            sx={{ width: 90 }}
                            inputProps={{ min: 100, max: 900, step: 100 }}
                          />
                        </DesignControlRow>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Label headers
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <DesignControlRow
                          label="Background"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.label.background_color')}
                          onRevert={() => handleTableDesignPropertyRevert('header.label.background_color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.header.label.backgroundColor}
                            onChange={(e) => handleTableDesignPropertyChange('header.label.background_color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Text color"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.label.text_color')}
                          onRevert={() => handleTableDesignPropertyRevert('header.label.text_color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.header.label.textColor}
                            onChange={(e) => handleTableDesignPropertyChange('header.label.text_color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Font weight"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'header.label.font_weight')}
                          onRevert={() => handleTableDesignPropertyRevert('header.label.font_weight')}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={resolvedTableDesign.header.label.fontWeight}
                            onChange={(e) => handleTableDesignPropertyChange('header.label.font_weight', Number(e.target.value))}
                            sx={{ width: 90 }}
                            inputProps={{ min: 100, max: 900, step: 100 }}
                          />
                        </DesignControlRow>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Cells
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <DesignControlRow
                          label="Font size"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'cell.font_size')}
                          onRevert={() => handleTableDesignPropertyRevert('cell.font_size')}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={resolvedTableDesign.cell.fontSize}
                            onChange={(e) => handleTableDesignPropertyChange('cell.font_size', Number(e.target.value))}
                            sx={{ width: 90 }}
                            inputProps={{ min: 8, max: 24 }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Alignment"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'cell.alignment')}
                          onRevert={() => handleTableDesignPropertyRevert('cell.alignment')}
                        >
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={resolvedTableDesign.cell.alignment}
                              onChange={(e) => handleTableDesignPropertyChange('cell.alignment', e.target.value)}
                            >
                              <MenuItem value="left">Left</MenuItem>
                              <MenuItem value="center">Center</MenuItem>
                              <MenuItem value="right">Right</MenuItem>
                            </Select>
                          </FormControl>
                        </DesignControlRow>
                        <DesignControlRow
                          label="Gridlines"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'cell.gridlines_visible')}
                          onRevert={() => handleTableDesignPropertyRevert('cell.gridlines_visible')}
                        >
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={resolvedTableDesign.cell.gridlinesVisible ? 'visible' : 'hidden'}
                              onChange={(e) => handleTableDesignPropertyChange('cell.gridlines_visible', e.target.value === 'visible')}
                            >
                              <MenuItem value="visible">Visible</MenuItem>
                              <MenuItem value="hidden">Hidden</MenuItem>
                            </Select>
                          </FormControl>
                        </DesignControlRow>
                        <DesignControlRow
                          label="Gridline color"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'cell.border_color')}
                          onRevert={() => handleTableDesignPropertyRevert('cell.border_color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.cell.borderColor}
                            onChange={(e) => handleTableDesignPropertyChange('cell.border_color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Gridline weight"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'cell.border_width')}
                          onRevert={() => handleTableDesignPropertyRevert('cell.border_width')}
                        >
                          <TextField
                            size="small"
                            type="number"
                            value={resolvedTableDesign.cell.borderWidth}
                            onChange={(e) => handleTableDesignPropertyChange('cell.border_width', Number(e.target.value))}
                            sx={{ width: 90 }}
                            inputProps={{ min: 0, max: 10 }}
                          />
                        </DesignControlRow>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Significance
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <DesignControlRow
                          label="Colour"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'significance.color')}
                          onRevert={() => handleTableDesignPropertyRevert('significance.color')}
                        >
                          <input
                            type="color"
                            value={resolvedTableDesign.significance.color}
                            onChange={(e) => handleTableDesignPropertyChange('significance.color', e.target.value)}
                            style={{ width: 24, height: 24, border: 'none', cursor: 'pointer', padding: 0, background: 'none' }}
                          />
                        </DesignControlRow>
                        <DesignControlRow
                          label="Emphasis"
                          overridden={isTableDesignPropertyOverridden(data?.table_design, 'significance.emphasis')}
                          onRevert={() => handleTableDesignPropertyRevert('significance.emphasis')}
                        >
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={resolvedTableDesign.significance.emphasis}
                              onChange={(e) => handleTableDesignPropertyChange('significance.emphasis', e.target.value)}
                            >
                              <MenuItem value="none">None</MenuItem>
                              <MenuItem value="bold">Bold</MenuItem>
                              <MenuItem value="italic">Italic</MenuItem>
                            </Select>
                          </FormControl>
                        </DesignControlRow>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.5 }}>
                        Conditional formatting
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Applies to freq/column %/row % cells. First matching rule wins.
                      </Typography>
                      <ConditionalFormatRuleList
                        rules={conditionalFormatRules}
                        onAdd={handleAddConditionalFormatRule}
                        onUpdate={handleUpdateConditionalFormatRule}
                        onRemove={handleRemoveConditionalFormatRule}
                      />
                    </Box>
                </ConfigSection>
              </>
            )}
              </Box>

              {/* Template actions act across the whole design, so they sit in
                  a footer pinned under the scrolling sections. */}
              {data?.view_mode === 'graph' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', px: 1, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    size="small"
                    color="info"
                    onClick={() => setApplyTemplateOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Apply template
                  </Button>
                  <Button
                    size="small"
                    color="info"
                    onClick={handleOpenSaveTemplate}
                    sx={{ textTransform: 'none' }}
                  >
                    Save as template
                  </Button>
                  {data?.design && (
                    <Button
                      size="small"
                      color="info"
                      onClick={handleDesignRevertAll}
                      sx={{ textTransform: 'none' }}
                    >
                      Revert all to theme
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {editDataOpen && (
        <EditDataOverlay
          open
          customer={customer}
          job={tableJob}
          dashboardId={dashboardId}
          dashboardName={dashboardName}
          tablePath={tablePath}
          tableTitle={tablePath.split('/').pop()?.replace(/\.cbt$/, '')}
          onClose={() => setEditDataOpen(false)}
          onSaved={handleEditDataSaved}
        />
      )}

      {/* Save as chart template (#1050) — design only: chart type, the
          explicit ChartDesign overrides, colour overrides. Name + optional
          description. */}
      <Dialog open={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save as chart template</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Saves the chart's design — type, axis/gridline/legend/data-label/title styling, and colour overrides.
            Table selection and data are never included.
          </Typography>
          {/* #1050 polish: the button below disables silently when there is
              nothing to capture — this hint is the only explanation, so it
              must appear for exactly that reason (never merely because the
              name field is empty). Depends only on data.design/color_overrides,
              not on templateNameInput. */}
          {saveTemplateHasNothingToCapture && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              This chart has no design overrides yet — style it first, then save it as a template.
            </Typography>
          )}
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={templateNameInput}
            onChange={(e) => setTemplateNameInput(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            minRows={2}
            value={templateDescriptionInput}
            onChange={(e) => setTemplateDescriptionInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveTemplateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="info"
            disabled={!templateNameInput.trim() || saveTemplateHasNothingToCapture || createChartTemplateIsLoading}
            onClick={handleConfirmSaveTemplate}
          >
            Save template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply chart template (#1050) — applies the stored design onto this
          widget as fresh explicit overrides; confirms first if that would
          overwrite design work already on the widget. */}
      <Dialog open={applyTemplateOpen} onClose={() => setApplyTemplateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Apply chart template</DialogTitle>
        <DialogContent>
          {chartTemplates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No chart templates yet — use "Save as template" on a styled chart to create one.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {chartTemplates.map((template) => (
                <Button
                  key={template.id}
                  variant="outlined"
                  onClick={() => handleChooseTemplateToApply(template.id)}
                  sx={{ textTransform: 'none', justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  <Box>
                    <Typography variant="body2">{template.name}</Typography>
                    {template.description && (
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    )}
                  </Box>
                </Button>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyTemplateOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOverwriteOpen} onClose={() => setConfirmOverwriteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Overwrite existing chart design?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This chart already has design overrides or colour overrides. Applying this template will replace them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverwriteOpen(false)}>Cancel</Button>
          <Button variant="contained" color="info" onClick={handleConfirmOverwriteApply}>
            Overwrite
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

interface TableGraphWidgetRendererProps {
  data: TableGraphWidgetData
  customer: string
  job: string
  readOnly?: boolean
  globalFilter?: string
  /**
   * Per-job resolved filters for a MULTI-JOB dashboard (#1162): job id →
   * compound (dashboard AND page) filter. When present the widget takes its
   * OWN job's entry — a member job legitimately gets its own filter, not the
   * "different job" drop that single-job dashboards apply. Absent on
   * single-job dashboards, where `globalFilter` is used exactly as before.
   */
  jobFilters?: Record<string, string>
  colorMap?: Record<string, string>
  /**
   * Theme id → flattened colour map, for resolving this widget's own
   * brand_palette_key (#1193 — a live theme reference). Editor surfaces build
   * it from the loaded theme list; view/shared surfaces from the
   * server-embedded resolved_themes. A missing entry (deleted theme) simply
   * falls back to `colorMap` — the page's colours.
   */
  themeColorMaps?: Record<string, Record<string, string>>
  /**
   * Called with a series name when it is clicked in the graph legend. Only the
   * editor's preview passes this — on a published dashboard the legend stays
   * inert, because a viewer clicking it would be editing the widget.
   */
  onSeriesRemove?: (seriesName: string) => void
  /**
   * Broken table reference actions (#969 Part E). Only the dashboard editor
   * passes these — a viewer has no tree to pick a replacement from and
   * nothing to save "remove reference" into.
   */
  onSelectAlternative?: () => void
  onRemoveReference?: () => void
}

/**
 * Renderer component for displaying a table or graph widget.
 * Loads data via useTableData and passes it directly to PlatinumTableWrapper
 * to avoid the wrapper's URL-param state management causing re-render issues.
 */
export const TableGraphWidgetRenderer: React.FC<TableGraphWidgetRendererProps> = ({
  data,
  customer,
  job,
  readOnly,
  globalFilter,
  jobFilters,
  colorMap,
  themeColorMaps,
  onSeriesRemove,
  onSelectAlternative,
  onRemoveReference,
}) => {
  const widgetJob = data.job || job

  // "View table" citation link (#1135) — same modal the Gen AI insights
  // widgets use for their [Table: ...] citations. Chart mode hides the
  // underlying table entirely, so viewers in preview/shared contexts had no
  // way to see the numbers behind the chart. Table mode already shows the
  // table itself, so the link only makes sense in graph mode. No editor-only
  // props are involved — this works in read-only/shared dashboard contexts.
  const [citationModalOpen, setCitationModalOpen] = useState(false)
  const showViewTableLink = data.view_mode === 'graph' && Boolean(data.table_path)

  // #1052 gap 4: a dashboard-level filter is validated against the
  // DASHBOARD's job vartree, but a legacy (pre-#961) widget can carry its
  // own `data.job` that differs from it. Carbon does not error on a filter
  // referencing a variable absent from a job's vartree — it silently
  // returns HTTP 200 with every cell zeroed (confirmed empirically against
  // a live job, see PR discussion). So a mismatched-job widget must not
  // receive the dashboard filter at all; the widget instead runs
  // unfiltered and says so (below).
  //
  // On a MULTI-JOB dashboard (#1162) `jobFilters` is present and this widget
  // takes its OWN job's entry — a member job legitimately carries its own
  // filter, so there is no "different job" drop; a widget whose job has no
  // filter simply runs unfiltered. The single-job path below is unchanged.
  const isMultiJob = jobFilters !== undefined
  const jobMismatch = !isMultiJob && Boolean(data.job) && widgetJob !== job
  const filterDroppedForJobMismatch = jobMismatch && Boolean(globalFilter)
  const appliedFilter = isMultiJob
    ? (jobFilters[widgetJob] || '')
    : (filterDroppedForJobMismatch ? '' : (globalFilter || ''))
  // Signpost line (#1229 C6): job display name, page filter, quick filter for
  // this widget's job — the state must be visible, not inferred. Replaces the
  // earlier "Unfiltered — no filter for this job" chip: a job with no filter
  // now shows its name alone, which says the same thing.
  const signpost = useFilterSignpost(widgetJob)

  // Load table data directly - passing it via the data prop bypasses
  // PlatinumTableWrapper's auto-loading and URL-param state, which
  // otherwise resets during dashboard auto-save re-renders.
  const tableData = useTableData({
    customer: (data.table_path || data.spec) ? customer : '',
    job: (data.table_path || data.spec) ? widgetJob : '',
    table: data.table_path || '',
    filter: appliedFilter,
    spec: data.spec,
  })

  // Low base / low cell notes (#1647). Judged on the whole table under the
  // page filter (what "view table" opens), not on the selected slice.
  const lowBase = useMemo(() => lowBaseReport(tableData.data), [tableData.data])
  const showLowBaseNotes = lowBase.lowBase || lowBase.lowCell
  // A note opens that same table with the cells behind it marked, as a
  // provenance link opens its source cell (Adam, 9 Oct).
  const [lowBaseShown, setLowBaseShown] = useState<'base' | 'cell' | null>(null)
  const lowBaseMarked = lowBaseShown === 'base' ? lowBase.baseCells : lowBaseShown === 'cell' ? lowBase.codeCells : []
  const lowBaseNote = lowBaseShown === 'base'
    ? `Low base size: ${lowBase.baseCells.length} ${lowBase.baseCells.length === 1 ? 'base' : 'bases'} under ${LOW_BASE_THRESHOLD} marked`
    : `Low cell size: ${lowBase.codeCells.length} ${lowBase.codeCells.length === 1 ? 'cell' : 'cells'} under ${LOW_CELL_THRESHOLD} marked`

  // Tier 1 view filter (#968). Widgets saved before the selection model still
  // carry `range`; effectiveSelection converts those on the fly, so they keep
  // rendering the same slice until someone re-saves them.
  const resolvedSelection = useMemo(
    () => resolveSelection(effectiveSelection(data, tableData.data), tableData.data),
    [data.selection, data.range, tableData.data],
  )

  // Table-mode display pipeline (#969 Obs 12-15), all at the data-model seam
  // so the PPTX export and the graph engine inherit it without knowing it
  // exists. In order:
  //   1. the positional selection, GROUP-PRESERVING (a table without its
  //      nesting is not the same table — applySelectionToTableData re-lays
  //      the group spans instead of clearing them like the chart path);
  //   2. any legacy view_config hide list (retired on the first Select Data
  //      edit, still honoured until then), plus widths/freeze;
  //   3. the Data Type band SET (Obs 14, multi-band per #969 comment 1 item 2
  //      — display only);
  //   4. Switch Axis as a real transpose (Obs 13). No control offers this any
  //      more in table mode (#969 comment 2 item 3), but widgets that stored
  //      it keep rendering as they were saved;
  //   5. the two scoped group-label strips (#969 comment 2 item 4), applied
  //      AFTER the transpose so "rows" means the rows on screen.
  const isTableMode = (data.view_mode || 'table') !== 'graph'
  // #1808: sort order and bar labels apply to the bar-family charts only —
  // the editor offers them nowhere else, and a chart switched to a line
  // keeps its stored values without them taking effect.
  const isBarFamilyChart = !isTableMode && isBarFamilyChartType(data.chart_type || 'bar')
  const categoryLabelMode = isBarFamilyChart ? data.category_label : undefined

  const effectiveData = useMemo(() => {
    if (!tableData.data) return tableData.data
    if (!isTableMode) {
      // #1181: blank zero-base points BEFORE the selection strips the base
      // rows the blanking reads. Default ON — only an explicit false turns
      // it off, so existing charts open with it already on.
      const source = data.skip_zero_base !== false
        ? blankZeroBasePoints(tableData.data)
        : tableData.data
      // #1808: bar labels from the side-group headings, relabelled BEFORE
      // the selection drops the groups. No mode = the selection alone.
      return chartDataWithCategoryLabels(source, resolvedSelection, categoryLabelMode)
    }
    let next = applySelectionToTableData(tableData.data, resolvedSelection)
    next = applyViewConfigToPlatinumData(next, data.view_config)
    next = applyTableDataType(next, resolveTableDataTypes(data.data_types, data.data_type))
    if (resolvedSelection.switchAxis) next = transposePlatinumData(next)
    const groupLabels = resolveGroupLabelVisibility(data.view_config)
    if (!groupLabels.rows) next = stripRowGroupLabels(next)
    if (!groupLabels.columns) next = stripColumnGroupLabels(next)
    return next
  }, [tableData.data, resolvedSelection, isTableMode, data.view_config, data.data_type, data.data_types, data.skip_zero_base, categoryLabelMode])

  // Widths are keyed by column path, which survives a refresh; the renderer
  // wants column indices, which do not. Resolved against the FILTERED data,
  // because hiding a column moves every index after it. Under Switch Axis
  // stored widths are skipped entirely: their keys belong to the UNTRANSPOSED
  // top axis, and resolving them against the transposed axis is at best a
  // no-op and at worst applies a width to a row label that collides with a
  // stored key.
  const columnWidths = useMemo(
    () => (isTableMode && !resolvedSelection.switchAxis
      ? resolveColumnWidths(effectiveData, data.view_config)
      : undefined),
    [isTableMode, resolvedSelection.switchAxis, effectiveData, data.view_config],
  )

  // Colour resolution (#1182/#1193): page-level colorMap (job palette with
  // the page theme already merged over it) < this chart's OWN theme (a live
  // reference by id, resolved through themeColorMaps — a missing/deleted
  // theme resolves to nothing and the page colours show through) < hand-set
  // per-series overrides, which win over everything.
  const chartThemeMap = data.brand_palette_key ? themeColorMaps?.[data.brand_palette_key] : undefined
  const effectiveColorMap = useMemo(() => {
    if (!colorMap && !chartThemeMap && !data.color_overrides) return undefined
    return {
      ...colorMap,
      ...chartThemeMap,
      ...Object.fromEntries(
        Object.entries(data.color_overrides || {}).map(([k, v]) => [k.toLowerCase().trim(), v])
      ),
    }
  }, [colorMap, chartThemeMap, data.color_overrides])

  // Memoize view settings to avoid unnecessary re-renders
  const viewSettings = useMemo<TableViewSettingsData>(() => ({
    visibility: {
      frequency: false,
      column_percent: true,
      row_percent: false,
      significance: false,
    },
    vertical_coefficient: 1,
    horizontal_coefficient: 1,
    chart_type: data.view_mode === 'graph' ? (data.chart_type || 'bar') : 'bar',
    data_type: data.data_type || 'freq',
    // Rows and columns are already filtered out of effectiveData, so the
    // engine is handed the whole (sliced) table. In graph mode switch_axis
    // is a transpose the graph engine applies, so it travels as a view
    // setting; in table mode the transpose is already applied to the data
    // itself (transposePlatinumData above), so the flag must NOT travel too.
    selected_rows: [],
    selected_columns: [],
    switch_axis: isTableMode ? false : resolvedSelection.switchAxis,
    view_mode: data.view_mode || 'table',
  }), [data.view_mode, data.chart_type, data.data_type, resolvedSelection.switchAxis, isTableMode])

  if (!data.table_path && !data.spec) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <TableChartIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
        <Typography variant="body2">Table not configured</Typography>
      </Box>
    )
  }

  if (tableData.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  // Broken table reference (#969 Part E): the table was renamed, deleted, or
  // moved. Distinguished from a transient load failure (network blip, 500)
  // via isTableNotFoundError — only a genuine "gone" is flagged here, so a
  // network hiccup never permanently blanks the widget's configuration.
  if (tableData.isError) {
    if (isTableNotFoundError(tableData.error)) {
      const tableName = data.table_path
        ? data.table_path.split('/').pop()?.replace(/\.[^.]+$/, '') || data.table_path
        : (data.spec ? `${data.spec.top || '?'} × ${data.spec.side || '?'}` : 'Table')
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <TableChartIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {tableName} is no longer available.
          </Typography>
          {data.table_path && (
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
              Previously stored at: {data.table_path}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
            Please select a new table or create one.
          </Typography>
          {(onSelectAlternative || onRemoveReference) && (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1.5 }}>
              {onSelectAlternative && (
                <Button size="small" color="info" variant="outlined" onClick={onSelectAlternative}>
                  Select alternative
                </Button>
              )}
              {onRemoveReference && (
                <Button size="small" color="inherit" onClick={onRemoveReference}>
                  Remove reference
                </Button>
              )}
            </Box>
          )}
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">Failed to load table data</Typography>
      </Box>
    )
  }

  if (!effectiveData) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="body2">
          {readOnly ? 'Table data not available' : 'Failed to load table data'}
        </Typography>
      </Box>
    )
  }

  const tableName = data.table_path
    ? data.table_path.split('/').pop()?.replace(/\.[^.]+$/, '') || ''
    : ''
  const sideLabel = effectiveData.meta?.side
  const displayTitle = data.title ?? (sideLabel ? `${tableName} — ${sideLabel}` : tableName)
  // Chart title formatting (#1044) — graph mode only, and only the
  // explicitly-overridden properties: an untouched widget keeps the exact
  // sx it always had.
  const titleDesign = data.view_mode === 'graph' ? data.design?.title : undefined

  return (
    <Box sx={{ height: 400, display: 'flex', flexDirection: 'column' }}>
      {displayTitle && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            fontWeight: titleDesign?.font_weight ?? 600,
            color: titleDesign?.color ?? 'text.primary',
            textAlign: titleDesign?.align,
            fontSize: titleDesign?.font_size != null ? `${titleDesign.font_size}px` : undefined,
          }}
        >
          {displayTitle}
        </Typography>
      )}
      {filterDroppedForJobMismatch && (
        <Tooltip title="This widget's table is from a different job than the dashboard filter, so the filter can't be applied to it.">
          <Chip
            icon={<InfoOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            label="Not filtered — different job"
            size="small"
            color="info"
            variant="outlined"
            sx={{ alignSelf: 'flex-start', mb: 1, fontSize: '0.7rem' }}
          />
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <PlatinumTableWrapper
          data={effectiveData}
          hideControls={true}
          currentViewSettings={viewSettings}
          savedViewSettings={viewSettings}
          colorMap={effectiveColorMap}
          onLegendToggle={onSeriesRemove}
          design={data.design}
          sortOrder={isBarFamilyChart ? data.sort_order : undefined}
          columnWidths={columnWidths}
          disableSticky={isTableMode ? !isFrozen(data.view_config) : undefined}
          tableDesign={data.table_design}
          // #1177: the widget hands the chart a fixed-height slot (400 minus
          // title and view-table link). Without this the chart insists on
          // PlatinumGraphs' standalone 400px minimum and its bottom — x-axis
          // labels included — is clipped by the slot.
          respectParentHeight
        />
      </Box>
      {/* Footer row: the filter signpost (#1229 C6 — job, page filter, quick
          filter for THIS widget's job) on the left, "view table" on the
          right. Either alone still renders the row. */}
      {(showViewTableLink || signpost || showLowBaseNotes) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: signpost || showLowBaseNotes ? 'space-between' : 'center',
            gap: 1,
            mt: 0.5,
            minWidth: 0,
          }}
        >
          {signpost && <FilterSignpostLine {...signpost} />}
          {showLowBaseNotes && (
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              {lowBase.lowBase && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  color="warning.main"
                  underline="hover"
                  data-testid="low-base-note"
                  onClick={() => setLowBaseShown('base')}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left' }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
                  Low base size, please review table
                </Link>
              )}
              {lowBase.lowCell && (
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  color="warning.main"
                  underline="hover"
                  data-testid="low-cell-note"
                  onClick={() => setLowBaseShown('cell')}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textAlign: 'left' }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
                  Low cell size, please review table
                </Link>
              )}
            </Box>
          )}
          {showViewTableLink && (
            <Link
              component="button"
              type="button"
              variant="caption"
              color="info.main"
              underline="hover"
              onClick={() => setCitationModalOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              view table
            </Link>
          )}
          {lowBaseShown && (
            <TableCitationModal
              open
              onClose={() => setLowBaseShown(null)}
              tableName={data.title || data.table_path || ''}
              customer={customer}
              job={widgetJob}
              filter={appliedFilter}
              filterDropped={filterDroppedForJobMismatch}
              data={tableData.data}
              markedCells={lowBaseMarked}
              markedNote={lowBaseNote}
            />
          )}
          {showViewTableLink && (
            <TableCitationModal
              open={citationModalOpen}
              onClose={() => setCitationModalOpen(false)}
              tableName={data.table_path || ''}
              customer={customer}
              job={widgetJob}
              filter={appliedFilter}
              filterDropped={filterDroppedForJobMismatch}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default {
  Editor: TableGraphWidgetEditor,
  Renderer: TableGraphWidgetRenderer,
}
